import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/_core/hooks/useAuth";
import { useTheme, getDefaultTenantColors } from "@/contexts/ThemeContext";
import { toast } from "sonner";
import { 
  Palette, 
  Check, 
  Sparkles, 
  RotateCcw, 
  LayoutDashboard, 
  Receipt, 
  Building2, 
  CheckCircle2, 
  MousePointerClick,
  Layers,
  SlidersHorizontal,
  Sun,
  Moon,
  Monitor,
  Upload,
  Image as ImageIcon,
  Trash2
} from "lucide-react";

interface AdminThemeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DARK_PRESETS = [
  { label: "Laranja Neon", sublabel: "★ Padrão Referência", primary: "#ff6b00", secondary: "#38bdf8" },
  { label: "Azul Elétrico", sublabel: "× Briquezi Master", primary: "#3b82f6", secondary: "#a855f7" },
  { label: "Roxo Cyber", sublabel: "× Anota Briquezi", primary: "#a855f7", secondary: "#38bdf8" },
  { label: "Verde Esmeralda", sublabel: "× Terminal Clean", primary: "#10b981", secondary: "#059669" },
  { label: "Âmbar Dourado", sublabel: "× Ouro Matte", primary: "#f59e0b", secondary: "#d97706" },
  { label: "Magenta Neon", sublabel: "× Cyberpunk", primary: "#f43f5e", secondary: "#e11d48" },
  { label: "Ciano Tech", sublabel: "× Futurista", primary: "#06b6d4", secondary: "#3b82f6" },
  { label: "Grafite Neutro", sublabel: "× Minimalista", primary: "#94a3b8", secondary: "#64748b" },
];

const LIGHT_PRESETS = [
  { label: "Laranja", sublabel: "× Só Facilitei", primary: "#ea580c", secondary: "#0284c7" },
  { label: "Azul", sublabel: "× Briquezi Master", primary: "#2563eb", secondary: "#7c3aed" },
  { label: "Roxo", sublabel: "× Anota Briquezi", primary: "#8b5cf6", secondary: "#2563eb" },
  { label: "Verde", sublabel: "× Esmeralda", primary: "#16a34a", secondary: "#15803d" },
  { label: "Amarelo", sublabel: "× Âmbar", primary: "#d97706", secondary: "#b45309" },
  { label: "Rosa", sublabel: "× Magenta", primary: "#e11d48", secondary: "#be185d" },
  { label: "Marrom", sublabel: "× Cobre", primary: "#92400e", secondary: "#78350f" },
  { label: "Grafite", sublabel: "× Neutro", primary: "#475569", secondary: "#334155" },
];

export function AdminThemeModal({ open, onOpenChange }: AdminThemeModalProps) {
  const { user } = useAuth();
  const { theme, setTheme, branding, setCompanyBranding } = useTheme();

  const isAllowed = user?.role === "admin" || user?.role === "admin-interno";
  const tenantDefaults = getDefaultTenantColors();

  const [selectedThemeMode, setSelectedThemeMode] = useState<"light" | "dark">(theme || "light");
  const [primaryColor, setPrimaryColor] = useState<string>(tenantDefaults.primary);
  const [secondaryColor, setSecondaryColor] = useState<string>(tenantDefaults.secondary);
  const [logoPreview, setLogoPreview] = useState<string | null>(branding?.logoUrl || null);
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  const [activeTabPreview, setActiveTabPreview] = useState<string>("geral");
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setSelectedThemeMode(theme);
      const userPrimary = (user as any)?.primaryColor || branding?.primaryColor || (theme === "dark" ? "#ff6b00" : tenantDefaults.primary);
      const userSecondary = (user as any)?.secondaryColor || branding?.secondaryColor || (theme === "dark" ? "#38bdf8" : tenantDefaults.secondary);
      setPrimaryColor(userPrimary);
      setSecondaryColor(userSecondary);
      setLogoPreview(branding?.logoUrl || null);
      setLogoBase64(null);
    }
  }, [open, user, branding, theme, tenantDefaults.primary, tenantDefaults.secondary]);

  if (!isAllowed) return null;

  const activePresets = selectedThemeMode === "dark" ? DARK_PRESETS : LIGHT_PRESETS;

  const handleApplyPreset = (p: { primary: string; secondary: string }) => {
    setPrimaryColor(p.primary);
    setSecondaryColor(p.secondary);
  };

  const handleModeChange = (mode: "light" | "dark") => {
    setSelectedThemeMode(mode);
    setTheme(mode);
    if (mode === "dark" && primaryColor === tenantDefaults.primary) {
      setPrimaryColor("#ff6b00");
      setSecondaryColor("#38bdf8");
    } else if (mode === "light" && primaryColor === "#ff6b00") {
      setPrimaryColor(tenantDefaults.primary);
      setSecondaryColor(tenantDefaults.secondary);
    }
  };

  const handleResetToTenant = () => {
    if (selectedThemeMode === "dark") {
      setPrimaryColor("#ff6b00");
      setSecondaryColor("#38bdf8");
    } else {
      setPrimaryColor(tenantDefaults.primary);
      setSecondaryColor(tenantDefaults.secondary);
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 3 * 1024 * 1024) {
      toast.error("O arquivo de logotipo deve ter no máximo 3MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setLogoBase64(result);
      setLogoPreview(result);
      toast.success("Imagem de logotipo carregada para pré-visualização!");
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoBase64("");
    setLogoPreview(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: any = {
        primaryColor,
        secondaryColor,
        themePreference: selectedThemeMode,
      };

      if (logoBase64 !== null) {
        payload.logoBase64 = logoBase64;
        if (logoBase64 === "") {
          payload.logoUrl = "";
        }
      }

      const res = await fetch("/api.php?route=auth.updateTheme", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          json: payload,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error?.json?.message || data.error?.message || "Erro ao salvar tema.");
      }

      const returnedLogo = data.result?.data?.json?.logoUrl !== undefined 
        ? data.result.data.json.logoUrl 
        : (data.logoUrl !== undefined ? data.logoUrl : branding?.logoUrl);

      // Atualiza o contexto de tema e branding
      setTheme(selectedThemeMode);
      setCompanyBranding({
        ...branding,
        primaryColor,
        secondaryColor,
        themePreference: selectedThemeMode,
        logoUrl: returnedLogo,
      });

      toast.success(`Tema ${selectedThemeMode === "dark" ? "Modo Escuro Obsidian" : "Modo Claro"} configurado e aplicado com sucesso!`);
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Erro ao salvar cores.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2.5">
            <div 
              className="p-2 rounded-xl text-white shadow-xs"
              style={{ backgroundColor: primaryColor }}
            >
              <Palette className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold">Personalização Visual & Dark Mode</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                Escolha o modo de exibição da plataforma (Escuro Obsidian ou Claro) e personalize a paleta de acentos com alta precisão.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Seção 0: Modo de Exibição (Dark / Light) */}
          <div className="space-y-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Monitor className="h-3.5 w-3.5" style={{ color: primaryColor }} />
              1. Modo de Interface
            </span>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleModeChange("dark")}
                className={`p-3 rounded-xl border text-left transition-all flex items-center gap-3 cursor-pointer ${
                  selectedThemeMode === "dark"
                    ? "bg-[#181a20] border-[#ff6b00] ring-2 ring-[#ff6b00]/30 text-white shadow-md"
                    : "bg-muted/30 border-border opacity-70 hover:opacity-100"
                }`}
              >
                <div className="p-2 rounded-lg bg-[#20232c] text-[#ff6b00] border border-white/10 shrink-0">
                  <Moon className="h-5 w-5" />
                </div>
                <div>
                  <span className="text-xs font-bold block">Modo Escuro (Obsidian)</span>
                  <span className="text-[10px] text-muted-foreground">Fundo carvão profundo com acentos neon</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleModeChange("light")}
                className={`p-3 rounded-xl border text-left transition-all flex items-center gap-3 cursor-pointer ${
                  selectedThemeMode === "light"
                    ? "bg-card border-primary ring-2 ring-primary/30 shadow-md"
                    : "bg-muted/30 border-border opacity-70 hover:opacity-100"
                }`}
              >
                <div className="p-2 rounded-lg bg-amber-500/10 text-amber-600 border border-amber-500/20 shrink-0">
                  <Sun className="h-5 w-5" />
                </div>
                <div>
                  <span className="text-xs font-bold block">Modo Claro (Clássico)</span>
                  <span className="text-[10px] text-muted-foreground">Fundo suave e caixas brancas legíveis</span>
                </div>
              </button>
            </div>
          </div>

          {/* Seção 1: Paletas Sugeridas */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" style={{ color: primaryColor }} />
                2. Paletas de Cores {selectedThemeMode === "dark" ? "Darkmode" : "Lightmode"}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleResetToTenant}
                className="h-6 px-2 text-[10px] text-muted-foreground hover:text-foreground gap-1"
              >
                <RotateCcw className="h-3 w-3" /> Padrão Sugerido
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {activePresets.map((p) => {
                const isSelected = primaryColor.toLowerCase() === p.primary.toLowerCase();
                return (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => handleApplyPreset(p)}
                    className={`p-2.5 rounded-xl border text-left transition-all flex flex-col gap-1.5 bg-card hover:border-primary/60 cursor-pointer ${
                      isSelected
                        ? "ring-2 ring-offset-1 shadow-xs"
                        : "border-border/70 opacity-90 hover:opacity-100"
                    }`}
                    style={isSelected ? { borderColor: p.primary } : {}}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className="h-5 w-5 rounded-full border-2 border-white/50 shadow-xs"
                        style={{ backgroundColor: p.primary }}
                      />
                      {isSelected && <Check className="h-3 w-3 shrink-0" style={{ color: p.primary }} />}
                    </div>
                    <div>
                      <span className="text-xs font-bold block leading-tight">{p.label}</span>
                      <span className="text-[9px] text-muted-foreground leading-tight">{p.sublabel}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Seção 3: Seleção Manual Precisa */}
          <div className="border rounded-xl p-3.5 bg-muted/10 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <SlidersHorizontal className="h-3.5 w-3.5" />
                3. Ajuste Fino de Cores (Hexadecimal)
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div className="space-y-1">
                <Label className="text-xs font-semibold">
                  Cor Primária (Botões, Toggles, Abas Ativas, Destaques)
                </Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="h-8 w-11 rounded border border-border cursor-pointer bg-transparent p-0.5 shrink-0"
                  />
                  <Input
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    placeholder="#ff6b00"
                    className="font-mono text-xs uppercase h-8"
                    maxLength={7}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs font-semibold">
                  Cor Secundária (Módulo Fiscal & Gráficos)
                </Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    className="h-8 w-11 rounded border border-border cursor-pointer bg-transparent p-0.5 shrink-0"
                  />
                  <Input
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    placeholder="#38bdf8"
                    className="font-mono text-xs uppercase h-8"
                    maxLength={7}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Seção 4: Logotipo da Barra Lateral (Canto Esquerdo do Backend) */}
          <div className="border rounded-xl p-3.5 bg-muted/10 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <ImageIcon className="h-3.5 w-3.5" style={{ color: primaryColor }} />
                4. Logotipo do Topo Esquerdo (Barra Lateral / Backend)
              </span>
              {logoPreview && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveLogo}
                  className="h-6 px-2 text-[10px] text-destructive hover:bg-destructive/10 gap-1"
                >
                  <Trash2 className="h-3 w-3" /> Remover Logo
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 items-center">
              <div>
                <Label htmlFor="logo-upload-input" className="text-xs font-semibold block mb-1.5">
                  Selecionar Imagem do Logotipo (PNG, SVG, JPG, WebP)
                </Label>
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="logo-upload-input"
                    className="flex-1 flex items-center justify-center gap-2 py-2 px-3 border border-dashed border-border rounded-lg bg-background hover:bg-muted/50 cursor-pointer transition-colors text-xs font-medium"
                  >
                    <Upload className="h-4 w-4 text-muted-foreground" />
                    <span>Escolher arquivo...</span>
                  </label>
                  <input
                    id="logo-upload-input"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </div>
                <span className="text-[10px] text-muted-foreground mt-1 block">
                  Recomendado: imagem horizontal transparente (ex: 240x60px até 3MB).
                </span>
              </div>

              {/* Preview do Logotipo */}
              <div className="rounded-lg border p-2.5 bg-background flex flex-col items-center justify-center min-h-[70px]">
                <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider mb-1">
                  Pré-visualização do Topo
                </span>
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="Pré-visualização do logotipo"
                    className="max-h-10 max-w-full object-contain"
                  />
                ) : (
                  <span className="text-xs text-muted-foreground italic">
                    Utilizando logotipo padrão do White Label
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Seção 5: Mapa de Onde as Cores Mudam (Live Simulator) */}
          <div className="border rounded-xl p-4 bg-card space-y-3.5 shadow-xs">
            <div className="flex items-center justify-between border-b pb-2">
              <span className="text-xs font-bold text-foreground flex items-center gap-1.5">
                <Layers className="h-4 w-4" style={{ color: primaryColor }} />
                5. Mapeamento Visual: Onde a cor é aplicada na interface
              </span>
              <span
                className="text-[10px] font-bold px-2.5 py-0.5 rounded-full text-white shadow-xs"
                style={{ backgroundColor: primaryColor }}
              >
                Simulação Ativa
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 pt-1">
              {/* Elemento 1: Item Selecionado do Menu Lateral */}
              <div className="border rounded-lg p-3 bg-muted/20 space-y-1.5">
                <span className="text-[10px] font-bold text-muted-foreground uppercase flex items-center gap-1">
                  <MousePointerClick className="h-3 w-3" /> Menu Lateral (Item Selecionado)
                </span>
                <div
                  className="flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold border-l-4 transition-all"
                  style={{
                    backgroundColor: `${primaryColor}18`,
                    color: primaryColor,
                    borderColor: primaryColor,
                  }}
                >
                  <div className="flex items-center gap-2">
                    <LayoutDashboard className="h-4 w-4" style={{ color: primaryColor }} />
                    <span>Painel Administrativo</span>
                  </div>
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: primaryColor }} />
                </div>
              </div>

              {/* Elemento 2: Botão de Ação Primária */}
              <div className="border rounded-lg p-3 bg-muted/20 space-y-1.5">
                <span className="text-[10px] font-bold text-muted-foreground uppercase flex items-center gap-1">
                  <Receipt className="h-3 w-3" /> Botão de Ação Primária
                </span>
                <button
                  type="button"
                  className="w-full py-2 px-3 rounded-lg text-xs font-semibold text-white shadow-xs flex items-center justify-center gap-1.5 transition-transform active:scale-98"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Receipt className="h-3.5 w-3.5" />
                  Emitir Nota Fiscal
                </button>
              </div>

              {/* Elemento 3: Abas Selecionadas (Tabs) */}
              <div className="border rounded-lg p-3 bg-muted/20 space-y-1.5">
                <span className="text-[10px] font-bold text-muted-foreground uppercase flex items-center gap-1">
                  <Layers className="h-3 w-3" /> Abas & Seções Selecionadas
                </span>
                <div className="grid grid-cols-2 gap-1 p-1 bg-muted rounded-lg text-xs">
                  {["geral", "fiscal"].map((t) => {
                    const isTabActive = activeTabPreview === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setActiveTabPreview(t)}
                        className={`py-1.5 text-center font-medium rounded-md transition-all cursor-pointer ${
                          isTabActive
                            ? "bg-background font-bold shadow-xs"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                        style={isTabActive ? { color: primaryColor, borderBottom: `2px solid ${primaryColor}` } : {}}
                      >
                        {t === "geral" ? "Dados Gerais" : "Parâmetros Fiscais"}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Elemento 4: Contorno de Foco e Badges */}
              <div className="border rounded-lg p-3 bg-muted/20 space-y-1.5">
                <span className="text-[10px] font-bold text-muted-foreground uppercase flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Contornos, Foco & Badges
                </span>
                <div className="flex items-center justify-between gap-2 pt-0.5">
                  <span
                    className="text-[10px] font-bold px-2.5 py-1 rounded-md border"
                    style={{
                      backgroundColor: `${primaryColor}18`,
                      color: primaryColor,
                      borderColor: `${primaryColor}40`,
                    }}
                  >
                    Badge Ativo
                  </span>
                  <div
                    className="px-2.5 py-1 text-xs rounded-md border bg-background font-mono text-[11px]"
                    style={{ borderColor: primaryColor, outline: `2px solid ${primaryColor}30` }}
                  >
                    input:focus
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            style={{ backgroundColor: primaryColor }}
            className="text-white hover:opacity-90 font-semibold shadow-xs"
          >
            {saving ? "Salvando..." : "Salvar e Aplicar Tema"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
